/**
 * Frontend Error Sanitizer
 * Ensures no sensitive implementation details leak to user-facing error messages
 */

// Patterns that reveal implementation details
const SENSITIVE_PATTERNS = [
  // API Keys and tokens
  /sk-ant-[a-zA-Z0-9*]+/gi,
  /sk-[a-zA-Z0-9*]+/gi,
  /pk_test_[a-zA-Z0-9*]+/gi,
  /pk_live_[a-zA-Z0-9*]+/gi,
  /whsec_[a-zA-Z0-9*]+/gi,
  
  // Provider URLs
  /https?:\/\/platform\.openai\.com[^\s]*/gi,
  /https?:\/\/console\.anthropic\.com[^\s]*/gi,
  /https?:\/\/dashboard\.stripe\.com[^\s]*/gi,
  /https?:\/\/console\.firebase\.google\.com[^\s]*/gi,
  
  // Provider names in error context
  /OpenAI/gi,
  /Anthropic/gi,
  /Claude/gi,
  /Stripe/gi,
  /Firebase/gi,
  /Firestore/gi,
  /JSearch/gi,
  /RapidAPI/gi,
  /GPT-4/gi,
  
  // Technical details
  /at https?:\/\/[^\s]+/gi,
  /You can find your API key[^\.]*/gi,
  
  // Internal paths
  /functions\/[^\s]+\.js/gi,
  /node_modules\/[^\s]+/gi,
  
  // Stack traces
  /at [A-Za-z]+\.[A-Za-z]+ \([^\)]+\)/gi
];

// Map of technical errors to user-friendly messages
const ERROR_MAPPINGS = {
  'incorrect api key': 'Service error. Please try again or contact support.',
  'invalid api key': 'Service error. Please try again or contact support.',
  'api key': 'Service error. Please try again or contact support.',
  'authentication failed': 'Authentication error. Please try again.',
  'rate limit': 'Service is busy. Please try again in a few minutes.',
  'quota exceeded': 'Service limit reached. Please try again later.',
  'timeout': 'Request took too long. Please try again.',
  'deadline exceeded': 'Request took too long. Please try again.',
  'publishable api key': 'Payment error. Please try again or contact support.',
  'secret api key': 'Payment error. Please try again or contact support.',
  'permission denied': 'Access denied. Please log in and try again.',
  'internal': 'An error occurred. Please try again.',
  'network': 'Connection error. Please check your internet and try again.'
};

/**
 * Sanitize an error message for user display
 * @param {string|Error} error - The error message or Error object
 * @returns {string} User-friendly error message
 */
export function sanitizeError(error) {
  // Extract message from Error object if needed
  let message = typeof error === 'string' ? error : (error?.message || 'An error occurred');
  
  // Check for mapped friendly messages first
  const lowerMessage = message.toLowerCase();
  for (const [pattern, friendlyMessage] of Object.entries(ERROR_MAPPINGS)) {
    if (lowerMessage.includes(pattern)) {
      return friendlyMessage;
    }
  }
  
  // Remove sensitive patterns
  for (const pattern of SENSITIVE_PATTERNS) {
    message = message.replace(pattern, '');
  }
  
  // Clean up
  message = message
    .replace(/\s+/g, ' ')
    .replace(/:\s*$/g, '')
    .replace(/\s*\.\s*$/g, '.')
    .trim();
  
  // If message is empty or too short, use generic
  if (!message || message.length < 5) {
    return 'An error occurred. Please try again.';
  }
  
  // Limit message length
  if (message.length > 150) {
    message = message.substring(0, 150) + '...';
  }
  
  return message;
}

/**
 * Get a user-friendly error message and log full error for debugging
 * @param {Error} error - The original error
 * @param {string} context - What operation failed
 * @returns {string} User-friendly error message
 */
export function handleError(error, context = 'Operation') {
  // Log full error for debugging (only visible in dev console)
  console.error(`[${context}] Error:`, error);
  
  // Return sanitized message for display
  return sanitizeError(error);
}

export default sanitizeError;

